import { globalConfirm } from '../../../components/ConfirmDialog';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import api, { getApiErrorMessage } from '../../../services/api';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { Plus, Trash2, ChevronRight, X, FileText, Check, Upload, Eye, Send, CheckCircle, XCircle, BookOpen, ExternalLink, RefreshCw } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { LoadingSpinner, EmptyState } from '../../../components/layout/SharedComponents';
import TypeAheadLookup from '../../../components/TypeAheadLookup';

import { TemplatePicker, SupplierSelect, ContractManagerSelect, ApprovalRouteBuilder, ContractDetailsStep, ContractWritingStep, ApprovalHistory } from './ContractHelpers';
import { EMPTY_FORM, STATUS_CONFIG, WorkflowBadge, GOVERNING_LAW, PAYMENT_TERMS } from '../contractConstants';
import { useDecimalFormat } from '../../../contexts/ExpenditureLabelsContext';

export function ExistingContractModal({ open, onClose, contractNumber, onSaved }) {
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [body, setBody] = useState('');
  const [approvalHistory, setApprovalHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [actionNote, setActionNote] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [tmplOpen, setTmplOpen] = useState(false);
  const [applying, setApplying] = useState(false);
  const [reviewStages, setReviewStages] = useState([]);
  const [showSendToSupplier, setShowSendToSupplier] = useState(false);
  const [supplierReviewEmail, setSupplierReviewEmail] = useState('');
  const [supplierReviewName, setSupplierReviewName] = useState('');
  const [supplierReviewMsg, setSupplierReviewMsg] = useState('');
  const [sendingToSupplier, setSendingToSupplier] = useState(false);
  const [reviewSessions, setReviewSessions] = useState([]);
  const [viewingSupplierReview, setViewingSupplierReview] = useState(null);
  const [sigName, setSigName] = useState('');
  const [sigEmail, setSigEmail] = useState('');
  const [sigPosition, setSigPosition] = useState('');
  const [sigLoaded, setSigLoaded] = useState(false);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [defaultStageGroups, setDefaultStageGroups] = useState({});
  const [contractStageGroups, setContractStageGroups] = useState({});
  const [auditTrail, setAuditTrail] = useState([]);
  const [systemTz, setSystemTz] = useState('America/Los_Angeles');

  const fmtTz = (d) => {
    if (!d) return '';
    try { return new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: systemTz }); }
    catch { return new Date(d).toLocaleString(); }
  };

  const load = useCallback(async () => {
    if (!open || !contractNumber) return;
    setLoading(true);
    try {
      const [cr, br, hr, sgr, rvr, atr] = await Promise.all([
        api.get(`/api/data/supplier-contracts/${contractNumber}`),
        api.get(`/api/contracts/${contractNumber}/body`),
        api.get(`/api/contracts/${contractNumber}/approvals`),
        api.get('/api/contracts/admin-settings/stage-groups'),
        api.get(`/api/contracts/${contractNumber}/review`),
        api.get(`/api/contracts/${contractNumber}/audit-trail`),
      ]);
      // Load system timezone
      try { const sysR = await api.get('/api/system/settings'); setSystemTz(sysR.data?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone); } catch {}
      setForm({ ...EMPTY_FORM, ...cr.data });
      const formData = { ...EMPTY_FORM, ...cr.data };
      setBody(resolveBodyVariables(br.data.body || '', formData));
      setApprovalHistory(hr.data);
      setReviewStages(sgr.data.review_stages || []);
      setAvailableGroups(sgr.data.available_groups || []);
      setDefaultStageGroups(sgr.data.stage_groups || {});
      setContractStageGroups(rvr.data.contract_stage_groups || {});
      setAuditTrail(atr.data.entries || []);
    } catch (err) { if (err?.response?.status >= 500) toast.error('Failed to load'); }
    setLoading(false);
  }, [open, contractNumber]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (open && contractNumber) loadReviewSessions(); }, [open, contractNumber]); // eslint-disable-line

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const { fmtVal } = useDecimalFormat();

  const resolveBodyVariables = (html, d) => {
    if (!html || !d) return html;
    const vars = {
      '{{contract_number}}': d.contract_number || '',
      '{{description}}': d.description || '',
      '{{supplier_code}}': d.supplier_code || '',
      '{{supplier_name}}': d.supplier_name || '',
      '{{contract_value}}': d.contract_value ? Number(d.contract_value).toLocaleString('en-US', { minimumFractionDigits: 2 }) : '',
      '{{currency}}': d.currency || '',
      '{{contract_manager}}': d.contract_manager || '',
      '{{contract_start}}': d.contract_start ? new Date(d.contract_start).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'America/Los_Angeles' }) : '',
      '{{contract_end}}': d.contract_end ? new Date(d.contract_end).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'America/Los_Angeles' }) : '',
      '{{review_date}}': d.review_date ? new Date(d.review_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'America/Los_Angeles' }) : '',
      '{{notice_period}}': d.notice_period ? String(d.notice_period) : '',
      '{{payment_terms}}': d.payment_terms || '',
      '{{governing_law}}': d.governing_law || '',
      '{{contract_renewable}}': d.contract_renewable === 'yes' ? 'Yes' : d.contract_renewable === 'no' ? 'No' : '',
      '{{effective_date_type}}': d.effective_date_type === 'on_execution' ? 'On Execution' : d.effective_date_type === 'on_specific_date' ? 'On Specific Date' : '',
      '{{effective_date}}': d.effective_date_type === 'on_execution' ? 'On Execution' : d.contract_start ? new Date(d.contract_start).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'America/Los_Angeles' }) : '',
      '{{tax_terms}}': d.tax_terms === 'all_taxes_included' ? 'All Taxes Included' : d.tax_terms === 'as_per_payment_terms' ? 'As Per Payment Terms' : '',
      '{{today}}': new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'America/Los_Angeles' }),
    };
    let result = html;
    for (const [key, val] of Object.entries(vars)) {
      if (val) result = result.split(key).join(val);
    }
    return result;
  };

  const sendToSupplier = async () => {
    if (!supplierReviewEmail.trim()) { toast.error('Supplier email required'); return; }
    setSendingToSupplier(true);
    try {
      await api.put(`/api/contracts/${contractNumber}/body`, { body });
      const r = await api.post(`/api/contract-review/send/${contractNumber}`, {
        supplier_email: supplierReviewEmail,
        supplier_name: supplierReviewName || form.supplier_name || '',
        message: supplierReviewMsg,
      });
      toast.success(r.data.message);
      setShowSendToSupplier(false);
      setSupplierReviewEmail('');
      setSupplierReviewMsg('');
      load();
      onSaved();
      loadReviewSessions();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Send failed')); }
    setSendingToSupplier(false);
  };

  const loadReviewSessions = () => {
    api.get(`/api/contract-review/sessions/${contractNumber}`).then(r => setReviewSessions(r.data.sessions || [])).catch(() => {});
  };

  const loadSignatoryDefaults = async () => {
    if (sigLoaded) return;
    if (form.supplier_code) {
      try {
        const r = await api.get(`/api/data/suppliers/${form.supplier_code}`);
        const contacts = r.data?.contacts || [];
        const primary = contacts.find(c => c.is_main) || contacts[0];
        if (primary) {
          setSigName(primary.name || '');
          setSigEmail(primary.email || '');
          setSigPosition(primary.position || '');
        }
      } catch {}
    }
    setSigLoaded(true);
  };

  const saveDetails = async () => {
    try { await api.put(`/api/data/supplier-contracts/${form.contract_number}`, form); onSaved(); }
    catch {}
  };

  const saveBody = async () => {
    try { await api.put(`/api/contracts/${form.contract_number}/body`, { body }); }
    catch {}
  };

  // Auto-save details when form changes (debounced 2s) — skip if executed
  const detailsTimerRef = useRef(null);
  const formLoadedRef = useRef(false);
  useEffect(() => {
    if (!form.contract_number || !formLoadedRef.current || form.workflow_status === 'EXECUTED') { formLoadedRef.current = !!form.contract_number; return; }
    if (detailsTimerRef.current) clearTimeout(detailsTimerRef.current);
    detailsTimerRef.current = setTimeout(() => { saveDetails(); }, 2000);
    return () => { if (detailsTimerRef.current) clearTimeout(detailsTimerRef.current); };
  }, [form]); // eslint-disable-line

  // Auto-save body when it changes (debounced 2s) — skip if executed
  const bodyTimerRef = useRef(null);
  const bodyLoadedRef = useRef(false);
  useEffect(() => {
    if (!form.contract_number || !body || !bodyLoadedRef.current || form.workflow_status === 'EXECUTED') { bodyLoadedRef.current = !!body; return; }
    if (bodyTimerRef.current) clearTimeout(bodyTimerRef.current);
    bodyTimerRef.current = setTimeout(() => { saveBody(); }, 2000);
    return () => { if (bodyTimerRef.current) clearTimeout(bodyTimerRef.current); };
  }, [body]); // eslint-disable-line

  const doAction = async (action) => {
    if (action === 'reject' && !actionNote.trim()) { toast.error('Rejection reason required'); return; }
    setActionLoading(true);
    try {
      const ep = action === 'submit' ? 'submit-for-review' : action === 'approve' ? 'approve' : 'reject';
      const payload = { note: actionNote };
      const r = await api.post(`/api/contracts/${form.contract_number}/${ep}`, payload);
      toast.success(r.data.message);
      setActionNote('');
      load();
      onSaved();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed')); }
    setActionLoading(false);
  };

  const handleDocUpload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append('file', file); fd.append('description', '');
    try {
      await api.post(`/api/data/supplier-contracts/${form.contract_number}/documents`, fd);
      toast.success('Document uploaded'); load();
    } catch (err) { if (err?.response?.status >= 500) toast.error('Upload failed'); }
  };

  const handleTemplateInsert = async (templateContent) => {
    setApplying(true);
    try {
      const res = await api.post('/api/contracts/resolve-smart-fields', {
        content: templateContent,
        contract_number: contractNumber,
        overrides: { GOVERNING_LAW: form.governing_law, PAYMENT_TERMS: form.payment_terms },
      });
      setBody(res.data.content);
      toast.success('Template inserted with smart fields applied');
    } catch (err) { if (err?.response?.status >= 500) toast.error('Failed'); }
    setApplying(false);
  };

  const wfStatus = form.workflow_status || 'DRAFT';
  const canEdit = !['EXECUTED', 'EXECUTED_BY_CP', 'SENT_FOR_EXECUTION'].includes(wfStatus);
  const wasApproved = wfStatus === 'APPROVED';
  const currentReviewStage = form.review_stage || 'DRAFT';
  const currentStageLabel = reviewStages.find(s => s.id === currentReviewStage)?.label || (currentReviewStage === 'DRAFT' ? 'Draft' : currentReviewStage === 'EXECUTED' ? 'Executed' : currentReviewStage);
  const allStageIds = ['DRAFT', ...reviewStages.map(s => s.id), 'EXECUTED'];
  const currentIdx = allStageIds.indexOf(currentReviewStage);
  const nextStageId = currentIdx >= 0 && currentIdx < allStageIds.length - 1 ? allStageIds[currentIdx + 1] : null;
  const nextStageLabel = nextStageId ? (reviewStages.find(s => s.id === nextStageId)?.label || (nextStageId === 'EXECUTED' ? 'Executed' : nextStageId)) : 'Review';
  const [bodyChanged, setBodyChanged] = useState(false);
  const [originalBody, setOriginalBody] = useState('');

  // Track if body has changed from the loaded version (for re-approval detection)
  useEffect(() => { if (body && !originalBody) setOriginalBody(body); }, [body, originalBody]);
  const handleBodyChange = (newBody) => {
    setBody(newBody);
    if (wasApproved && originalBody && newBody !== originalBody) setBodyChanged(true);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-[95vw] xl:max-w-6xl h-[90vh] flex flex-col p-0">
          <div className="flex-1 overflow-hidden flex flex-col">
          <div className="flex items-center justify-between px-6 py-4 border-b sticky top-0 bg-card z-10">
            <div className="flex items-center gap-3">
              <h2 className="text-base font-semibold" style={{ fontFamily: 'Space Grotesk' }}>
                Contract — {form.contract_number}
              </h2>
              <WorkflowBadge status={wfStatus} reviewStage={currentReviewStage} stageLabel={currentStageLabel} />
            </div>
            <div className="flex items-center gap-2 mr-6">
              <span className="text-sm text-muted-foreground">Active</span>
              <Switch checked={form.active} onCheckedChange={async (v) => {
                f('active', v);
                try { await api.put(`/api/data/supplier-contracts/${contractNumber}`, { active: v }); toast.success(v ? 'Contract activated' : 'Contract deactivated'); onSaved(); }
                catch { toast.error('Failed to save'); }
              }} />
            </div>
          </div>

          {loading ? <div className="py-16"><LoadingSpinner /></div> : (
            <Tabs defaultValue="details" className="flex-1 flex flex-col overflow-hidden">
              <TabsList className="mx-6 mt-4 flex-shrink-0">
                <TabsTrigger value="details">Details</TabsTrigger>
                <TabsTrigger value="write">Contract Writing</TabsTrigger>
                <TabsTrigger value="approvals">Approvals {approvalHistory.length > 0 && `(${approvalHistory.length})`}</TabsTrigger>
                <TabsTrigger value="documents">Documents ({(form.documents || []).length})</TabsTrigger>
                {(wfStatus === 'EXECUTED' || wfStatus === 'EXECUTED_BY_CP') && <TabsTrigger value="executed">Executed Contract</TabsTrigger>}
              </TabsList>

              {/* ── Writing tab (first / default) ── */}
              <TabsContent value="write" className="px-6 py-4 flex-1 overflow-y-auto">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-sm font-medium">{form.description}</p>
                    <p className="text-xs text-muted-foreground">
                      {wasApproved && !bodyChanged ? 'This contract is approved. Editing will require re-approval.' : 'Edit the contract content below.'}
                    </p>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => setTmplOpen(true)}>
                    <BookOpen className="h-4 w-4 mr-1" /> Load Template
                  </Button>
                </div>
                {wasApproved && bodyChanged && (
                  <div className="mb-3 px-4 py-3 rounded-lg bg-amber-50 border border-amber-200">
                    <p className="text-sm font-semibold text-amber-700">Changes detected on approved contract</p>
                    <p className="text-xs text-amber-600 mt-1">This contract has been approved but you've made changes. You will need to submit it for re-approval.</p>
                  </div>
                )}
                <ContractWritingStep body={body} onChange={handleBodyChange} isReadOnly={!canEdit} contractNumber={contractNumber} contractData={form} />
                <div className="flex justify-end mt-3 gap-2">
                  <span className="text-xs text-muted-foreground italic">Auto-saved</span>
                </div>
              </TabsContent>

              {/* ── Details tab ── */}
              <TabsContent value="details" className="px-6 py-4 flex-1 overflow-y-auto">
                <div className="grid grid-cols-2 gap-4">

                  {/* Contract Number — read-only */}
                  <div className="space-y-1.5">
                    <Label>Contract Number</Label>
                    <Input value={form.contract_number || ''} disabled />
                  </div>

                  {/* Description */}
                  <div className="space-y-1.5">
                    <Label>Description *</Label>
                    <Input value={form.description || ''} onChange={e => f('description', e.target.value)} />
                  </div>

                  {/* Counterparty/Supplier Code */}
                  <div className="space-y-1.5">
                    <Label>Counterparty/Supplier Code</Label>
                    {['UNDER_REVIEW','IN_VENDOR_REVIEW','REVIEWING_VENDOR','CP_REVIEW_COMPLETE','APPROVED'].includes(wfStatus) ? (
                      <p className="text-sm h-9 flex items-center">{form.supplier_code || '—'}</p>
                    ) : (
                      <TypeAheadLookup
                        endpoint="/api/data/suppliers"
                        valueField="supplier_code"
                        labelField="supplier_name"
                        value={form.supplier_code}
                        displayValue={form.supplier_code}
                        placeholder="Search suppliers..."
                        onChange={(code, rec) => {
                          f('supplier_code', code);
                          f('supplier_name', rec?.supplier_name || '');
                        }}
                      />
                    )}
                  </div>

                  {/* Counterparty/Supplier Name */}
                  <div className="space-y-1.5">
                    <Label>Counterparty/Supplier Name</Label>
                    <Input value={form.supplier_name || ''} readOnly
                      className="bg-muted/40 cursor-not-allowed"
                      placeholder="Auto-filled from supplier code" />
                  </div>

                  {/* Contract Value + Currency side by side */}
                  <div className="space-y-1.5">
                    <Label>Contract Value</Label>
                    <div className="flex gap-2">
                      <div className="flex-1 relative">
                        <Input
                          type="text"
                          value={form.contract_value || ''}
                          onChange={e => { const v = e.target.value.replace(/[^0-9.]/g, ''); f('contract_value', v ? parseFloat(v) : 0); }}
                          className="flex-1"
                          placeholder="0.00"
                        />
                        {form.contract_value > 0 && (
                          <p className="text-[10px] text-muted-foreground mt-0.5">{fmtVal(form.contract_value)}</p>
                        )}
                      </div>
                      {/* Currency code lookup */}
                      <div className="w-28 flex-shrink-0">
                        <TypeAheadLookup
                          endpoint="/api/data/currencies"
                          valueField="code"
                          labelField="description"
                          value={form.currency || 'USD'}
                          displayValue={form.currency || 'USD'}
                          placeholder="USD"
                          onChange={(code) => f('currency', code)}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Contract Manager — mandatory */}
                  <div className="space-y-1.5">
                    <Label className="text-red-500 font-medium">Contract Manager *</Label>
                    <ContractManagerSelect
                      value={form.contract_manager || ''}
                      onChange={v => f('contract_manager', v)}
                    />
                  </div>

                  {/* Dates */}
                  <div className="space-y-1.5">
                    <Label>Contract Start</Label>
                    <Input type="date" value={form.contract_start ? form.contract_start.split('T')[0] : ''} onChange={e => f('contract_start', e.target.value)} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Contract End</Label>
                    <Input type="date" value={form.contract_end ? form.contract_end.split('T')[0] : ''} onChange={e => {
                      f('contract_end', e.target.value);
                      // Auto-calc review date = end - notice_period days
                      if (e.target.value && form.notice_period) {
                        const end = new Date(e.target.value);
                        const review = new Date(end.getTime() - (form.notice_period || 90) * 86400000);
                        f('review_date', review.toISOString().split('T')[0]);
                      }
                    }} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Review Date <span className="text-xs text-muted-foreground">(auto: {form.notice_period || 90}d before end)</span></Label>
                    <Input type="date" value={form.review_date ? form.review_date.split('T')[0] : ''} onChange={e => f('review_date', e.target.value)} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Notice Period (days)</Label>
                    <Input type="number" value={form.notice_period || 90} onChange={e => {
                      const np = parseInt(e.target.value) || 90;
                      f('notice_period', np);
                      if (form.contract_end) {
                        const end = new Date(form.contract_end);
                        const review = new Date(end.getTime() - np * 86400000);
                        f('review_date', review.toISOString().split('T')[0]);
                      }
                    }} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Payment Terms</Label>
                    <Input value={form.payment_terms || ''} onChange={e => f('payment_terms', e.target.value)} placeholder="Net 30" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Governing Law</Label>
                    <Input value={form.governing_law || ''} onChange={e => f('governing_law', e.target.value)} placeholder="e.g. England & Wales" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Contract Renewable</Label>
                    <select value={form.contract_renewable || 'no'} onChange={e => f('contract_renewable', e.target.value)}
                      className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm">
                      <option value="yes">Yes</option>
                      <option value="no">No</option>
                    </select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Contract Effective Date</Label>
                    <select value={form.effective_date_type || 'on_specific_date'} onChange={e => f('effective_date_type', e.target.value)}
                      className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm">
                      <option value="on_specific_date">On Specific Date</option>
                      <option value="on_execution">On Execution</option>
                    </select>
                    {form.effective_date_type === 'on_specific_date' && (
                      <p className="text-[10px] text-muted-foreground mt-0.5">Uses Contract Start date: {form.contract_start ? new Date(form.contract_start).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'America/Los_Angeles' }) : 'Not set'}</p>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    <Label>Tax</Label>
                    <select value={form.tax_terms || 'all_taxes_included'} onChange={e => f('tax_terms', e.target.value)}
                      className="h-9 w-full rounded-lg border border-input bg-background px-3 text-sm">
                      <option value="all_taxes_included">All Taxes Included</option>
                      <option value="as_per_payment_terms">As Per Payment Terms</option>
                    </select>
                  </div>
                </div>

                <div className="flex justify-end gap-2 mt-5">
                  <Button variant="outline" onClick={() => toast.info('View Spend available once POs are raised against this contract')}>
                    <Eye className="h-4 w-4 mr-1" /> View Spend
                  </Button>
                  <Button variant="destructive" onClick={async () => {
                    if (!await globalConfirm('Delete this contract?', { title: 'Delete Contract', destructive: true })) return;
                    try {
                      await api.delete(`/api/data/supplier-contracts/${form.contract_number}`);
                      toast.success('Deleted'); onSaved(); onClose();
                    } catch { toast.error('Delete failed'); }
                  }}>Delete</Button>
                  <span className="text-xs text-muted-foreground italic">Auto-saved</span>
                </div>
              </TabsContent>

              {/* ── Approvals tab ── */}
              <TabsContent value="approvals" className="px-6 py-4 flex-1 overflow-y-auto">
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="border rounded-xl p-4 space-y-3">
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">Status:</span>
                        <WorkflowBadge status={wfStatus} reviewStage={currentReviewStage} stageLabel={currentStageLabel} />
                      </div>
                      {wfStatus === 'APPROVED' && currentReviewStage === 'EXECUTED' && <p className="text-xs text-green-700 bg-green-50 rounded-lg px-3 py-2">Fully Approved. Ready for signature via DocuSign or Internal Signing.</p>}
                      {wfStatus === 'APPROVED' && currentReviewStage !== 'EXECUTED' && <p className="text-xs text-green-700 bg-green-50 rounded-lg px-3 py-2">{currentStageLabel} — Approved.</p>}
                      {wfStatus === 'UNDER_REVIEW' && <p className="text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">{currentStageLabel} — Awaiting approval.</p>}
                      {wfStatus === 'IN_VENDOR_REVIEW' && <p className="text-xs text-purple-700 bg-purple-50 rounded-lg px-3 py-2">Contract is with the counterparty/supplier for review. Awaiting their response.</p>}
                      {wfStatus === 'REVIEWING_VENDOR' && <p className="text-xs text-indigo-700 bg-indigo-50 rounded-lg px-3 py-2">Counterparty has responded. Review their comments and changes below, then accept or reject.</p>}
                      {wfStatus === 'CP_REVIEW_COMPLETE' && <p className="text-xs text-teal-700 bg-teal-50 rounded-lg px-3 py-2">Counterparty review complete. Ready to submit for internal review.</p>}
                      {wfStatus === 'REJECTED' && <p className="text-xs text-red-700 bg-red-50 rounded-lg px-3 py-2">{currentStageLabel} — Rejected.</p>}
                    </div>
                    <div className="space-y-1.5">
                      <Label>Note / Comments</Label>
                      <Textarea value={actionNote} onChange={e => setActionNote(e.target.value)}
                        placeholder={wfStatus === 'UNDER_REVIEW' ? 'Review comments (required for rejection)...' : 'Note for reviewers...'}
                        rows={3} />
                    </div>
                    {/* Submit for Counterparty/Supplier Review — only when DRAFT with content and not yet reviewed */}
                    {(wfStatus === 'DRAFT' || wfStatus === 'REJECTED') && body.trim() && currentReviewStage === 'DRAFT' && !['CP_REVIEW_COMPLETE'].includes(wfStatus) && (
                      <Button className="w-full" variant="outline" onClick={async () => {
                        let email = '', name = form.supplier_name || '';
                        if (form.supplier_code) {
                          try {
                            const r = await api.get(`/api/data/suppliers/${form.supplier_code}`);
                            const contacts = r.data?.contacts || [];
                            const primary = contacts.find(c => c.is_main) || contacts[0];
                            if (primary) { email = primary.email || ''; name = primary.name || name; }
                          } catch {}
                        }
                        if (!email) {
                          toast.error('No supplier email found. Please ensure the supplier record has a valid contact email address.');
                          return;
                        }
                        setSupplierReviewEmail(email);
                        setSupplierReviewName(name);
                        setSupplierReviewMsg('Please review the attached contract.');
                        setShowSendToSupplier(true);
                      }} disabled={actionLoading} data-testid="submit-cp-review-btn">
                        <ExternalLink className="h-4 w-4 mr-2" /> Submit for Counterparty/Supplier Review
                      </Button>
                    )}
                    {/* After CP review complete — submit for internal review stages */}
                    {wfStatus === 'CP_REVIEW_COMPLETE' && body.trim() && (
                      <Button className="w-full" onClick={() => doAction('submit')} disabled={actionLoading}>
                        <Send className="h-4 w-4 mr-2" />{actionLoading ? 'Submitting...' : `Submit for ${nextStageLabel || 'Internal Review'}`}
                      </Button>
                    )}
                    {/* Submit for stage approval — when at a review stage (not DRAFT) */}
                    {(wfStatus === 'DRAFT' || wfStatus === 'REJECTED') && body.trim() && currentReviewStage !== 'DRAFT' && currentReviewStage !== 'EXECUTED' && (
                      <Button className="w-full" onClick={() => doAction('submit')} disabled={actionLoading}>
                        <Send className="h-4 w-4 mr-2" />{actionLoading ? 'Submitting...' : `Submit ${currentStageLabel} for Approval`}
                      </Button>
                    )}
                    {/* Show signature panel when contract is APPROVED at EXECUTED stage (all review stages complete) */}
                    {wfStatus === 'APPROVED' && currentReviewStage === 'EXECUTED' && body.trim() && (
                      <SignaturePanel contractNumber={contractNumber} form={form} loadDefaults={loadSignatoryDefaults}
                        sigName={sigName} setSigName={setSigName} sigEmail={sigEmail} setSigEmail={setSigEmail}
                        sigPosition={sigPosition} setSigPosition={setSigPosition}
                        onSent={() => { load(); onSaved(); }} />
                    )}
                    {(wfStatus === 'IN_VENDOR_REVIEW' || wfStatus === 'REVIEWING_VENDOR') && (
                      <p className="text-xs text-purple-600">Cannot submit for internal approval while counterparty review is in progress.</p>
                    )}
                    {(wfStatus === 'DRAFT' || wfStatus === 'REJECTED') && !body.trim() && (
                      <p className="text-xs text-amber-600">Write contract content before submitting.</p>
                    )}
                    {wfStatus === 'UNDER_REVIEW' && (
                      <div className="flex gap-2">
                        <Button className="flex-1 bg-green-600 hover:bg-green-700" onClick={() => doAction('approve')} disabled={actionLoading}>
                          <CheckCircle className="h-4 w-4 mr-1" />{actionLoading ? '...' : 'Approve'}
                        </Button>
                        <Button className="flex-1" variant="destructive" onClick={() => doAction('reject')} disabled={actionLoading}>
                          <XCircle className="h-4 w-4 mr-1" />{actionLoading ? '...' : 'Reject'}
                        </Button>
                      </div>
                    )}

                    {/* Signature status */}
                    {wfStatus === 'SENT_FOR_EXECUTION' && (
                      <div className="border rounded-xl p-3 bg-orange-50 space-y-3">
                        <p className="text-xs font-semibold text-orange-700">Sent to CP via {form.signature_method === 'docusign' ? 'DocuSign' : 'Internal Signing'}</p>
                        <p className="text-xs text-orange-600">Awaiting counterparty signature. Once signed, it will be sent to us for counter-signature.</p>
                        {form.signature_method === 'docusign' && (
                          <Button size="sm" variant="outline" className="w-full" onClick={async () => {
                            try {
                              const r = await api.post(`/api/signatures/check-docusign/${contractNumber}`);
                              if (r.data.status === 'completed') {
                                toast.success('DocuSign complete — contract is now Fully Executed!');
                                load(); onSaved();
                              } else if (r.data.status === 'cp_signed') {
                                toast.success('CP has signed! Contract is now with us for counter-signature.');
                                load(); onSaved();
                              } else if (r.data.status === 'sent') {
                                toast.info('Awaiting signatures — not yet signed by either party.');
                              } else if (r.data.status === 'delivered') {
                                toast.info('Documents delivered to signatories — awaiting their signatures.');
                              } else {
                                toast.info(`DocuSign status: ${r.data.status}`);
                              }
                            } catch { toast.error('Failed to check DocuSign status'); }
                          }} data-testid="check-docusign-btn">
                            <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Check DocuSign Status
                          </Button>
                        )}
                      </div>
                    )}

                    {wfStatus === 'EXECUTED_BY_CP' && (
                      <div className="border rounded-xl p-3 bg-lime-50 space-y-3">
                        <p className="text-xs font-semibold text-lime-700">CP Has Signed — Sent to Us for DocuSign</p>
                        <p className="text-xs text-lime-600">The counterparty has signed. Awaiting our counter-signature to fully execute the contract.</p>
                        {form.signature_method === 'docusign' && (
                          <Button size="sm" variant="outline" className="w-full" onClick={async () => {
                            try {
                              const r = await api.post(`/api/signatures/check-docusign/${contractNumber}`);
                              if (r.data.status === 'completed') {
                                toast.success('DocuSign complete — contract is now Fully Executed!');
                                load(); onSaved();
                              } else {
                                toast.info(`DocuSign status: ${r.data.status}`);
                              }
                            } catch { toast.error('Failed to check DocuSign status'); }
                          }} data-testid="check-docusign-btn-cp">
                            <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Check DocuSign Status
                          </Button>
                        )}
                      </div>
                    )}

                    {/* Dynamic Workflow */}
                    <div className="border rounded-xl p-3 bg-muted/20">
                      <p className="text-xs font-medium mb-2">Workflow</p>
                      <div className="flex items-center gap-1 text-xs flex-wrap">
                        {[{ id: 'DRAFT', label: 'Draft' }, ...reviewStages, { id: 'EXECUTED', label: 'Executed' }].map((s, i, arr) => {
                          const allIds = arr.map(x => x.id);
                          // When fully approved (APPROVED at EXECUTED), don't highlight EXECUTED pill — it's not signed yet
                          const effectiveCurrentStage = (wfStatus === 'APPROVED' && currentReviewStage === 'EXECUTED') 
                            ? allIds[allIds.length - 2] // last review stage before EXECUTED
                            : currentReviewStage;
                          const currentIdx = allIds.indexOf(effectiveCurrentStage);
                          const stageIdx = i;
                          const isCurrent = s.id === effectiveCurrentStage;
                          const isPast = stageIdx < currentIdx || (stageIdx === currentIdx && wfStatus === 'APPROVED' && currentReviewStage === 'EXECUTED');
                          const isFuture = stageIdx > currentIdx && !(stageIdx === currentIdx && wfStatus === 'APPROVED' && currentReviewStage === 'EXECUTED');
                          return (
                            <React.Fragment key={s.id}>
                              <span className={`px-2.5 py-1 rounded-full font-medium transition-all ${
                                isCurrent ? 'bg-blue-600 text-white shadow-sm ring-2 ring-blue-300' :
                                isPast ? 'bg-green-100 text-green-700 border border-green-300' :
                                'bg-muted text-muted-foreground'
                              }`}>{isPast ? '✓ ' : ''}{s.label}</span>
                              {i < arr.length - 1 && <span className={isPast ? 'text-green-400' : 'text-muted-foreground'}>→</span>}
                            </React.Fragment>
                          );
                        })}
                      </div>
                      <p className="text-[10px] text-muted-foreground mt-1.5">Rejected → returns to Draft for revision. Multiple rounds supported.</p>
                    </div>

                    {/* Stage Approval Groups — editable per contract */}
                    {reviewStages.length > 0 && (
                      <div className="border rounded-xl p-3">
                        <p className="text-xs font-medium mb-2">Stage Approval Groups</p>
                        <p className="text-[10px] text-muted-foreground mb-2">Assign an approval group to each stage for this contract. Defaults from system settings shown — override as needed.</p>
                        <div className="space-y-2">
                          {reviewStages.map(stage => {
                            const contractGroup = contractStageGroups[stage.id];
                            const defaultGroup = defaultStageGroups[stage.id];
                            // If contract override is explicitly set to __NONE__, show "No approval"
                            // If contract override has a group_code, use it
                            // Otherwise fall back to system default
                            const hasExplicitNone = contractGroup?.group_code === '__NONE__';
                            const effectiveGroup = hasExplicitNone ? '' : (contractGroup?.group_code || defaultGroup?.group_code || '');
                            const isOverridden = contractGroup && (contractGroup.group_code !== (defaultGroup?.group_code || ''));
                            return (
                              <div key={stage.id} className="flex items-center gap-2">
                                <span className="text-xs font-medium w-28 flex-shrink-0">{stage.label}</span>
                                <select value={effectiveGroup}
                                  onChange={e => {
                                    const val = e.target.value;
                                    // Empty string means user explicitly chose "No approval"
                                    setContractStageGroups(prev => ({...prev, [stage.id]: val ? { group_code: val } : { group_code: '__NONE__' }}));
                                  }}
                                  className={`flex-1 h-7 rounded-lg border px-2 text-xs ${isOverridden ? 'border-amber-300 bg-amber-50' : 'border-input bg-background'}`}>
                                  <option value="">No approval</option>
                                  {availableGroups.map(g => (
                                    <option key={g.group_code} value={g.group_code}>
                                      {g.group_code} ({g.routing_method === 'all_at_once' ? 'All' : g.routing_method === 'sequential' ? 'Seq' : 'Any'}, {(g.members||[]).length})
                                    </option>
                                  ))}
                                </select>
                                {isOverridden && <Badge className="text-[8px] bg-amber-100 text-amber-700">Override</Badge>}
                              </div>
                            );
                          })}
                        </div>
                        <div className="flex justify-end mt-2">
                          <Button size="sm" variant="outline" className="h-6 text-[10px]" onClick={async () => {
                            try {
                              await api.put(`/api/contracts/${contractNumber}/review/stage-groups`, { stage_groups: contractStageGroups });
                              toast.success('Stage groups saved for this contract');
                            } catch { toast.error('Save failed'); }
                          }}>Save Groups</Button>
                        </div>
                      </div>
                    )}
                  </div>
                  <div>
                    <h3 className="font-semibold mb-3 text-sm">Review History</h3>
                    <ApprovalHistory history={approvalHistory} fmtTz={fmtTz} />

                    {/* CP Review Sessions */}
                    {reviewSessions.length > 0 && (
                      <div className="mt-4">
                        <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">CP Reviews</h4>
                        <div className="space-y-2">
                          {reviewSessions.map((s, i) => {
                            const cpComplete = wfStatus === 'CP_REVIEW_COMPLETE';
                            const isLatestReviewed = !cpComplete && s.status === 'reviewed' && !s.dealt_with && i === reviewSessions.findIndex(x => x.status === 'reviewed' && !x.dealt_with);
                            const isOld = cpComplete || s.dealt_with || (s.status === 'reviewed' && !isLatestReviewed);
                            return (
                              <div key={i}
                                className={`p-2 rounded-lg border text-xs cursor-pointer transition-colors ${
                                  isLatestReviewed ? 'border-green-300 bg-green-50 hover:border-green-400' :
                                  isOld ? 'border-amber-300 bg-amber-50 hover:border-amber-400' :
                                  'border-amber-200 bg-amber-50'
                                }`}
                                onClick={() => s.status === 'reviewed' ? setViewingSupplierReview({ ...s, _editable: isLatestReviewed }) : null}>
                                <div className="flex items-center justify-between">
                                  <span className="font-semibold">{s.supplier_name} ({s.supplier_email})</span>
                                  <Badge className={
                                    isLatestReviewed ? 'bg-green-100 text-green-700 text-[9px]' :
                                    isOld ? 'bg-amber-100 text-amber-700 text-[9px]' :
                                    'bg-amber-100 text-amber-700 text-[9px]'
                                  }>
                                    {s.status !== 'reviewed' ? 'Pending' :
                                     isLatestReviewed ? 'CP Reviewed — Click to edit' :
                                     'CP Reviewed — View only'}
                                  </Badge>
                                </div>
                                <p className="text-muted-foreground mt-0.5">Sent by {s.sent_by} — {s.created_at ? fmtTz(s.created_at) : ''}</p>
                                {s.status === 'reviewed' && s.supplier_comments?.length > 0 && (
                                  <p className="mt-1 text-primary">{s.supplier_comments.length} comment(s)</p>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Contract Audit Trail */}
                    <div className="mt-4">
                      <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">Contract Audit Trail</h4>
                      {auditTrail.length === 0 ? (
                        <p className="text-xs text-muted-foreground py-2">No audit entries yet.</p>
                      ) : (
                        <div className="border rounded-lg divide-y max-h-[300px] overflow-y-auto">
                          {auditTrail.map((e, i) => (
                              <div key={i} className="px-3 py-2 text-xs hover:bg-accent/20">
                                <div className="flex items-center justify-between">
                                  <span className="font-semibold">{e.user}</span>
                                  <span className="text-[10px] text-muted-foreground">
                                    {e.timestamp ? fmtTz(e.timestamp) : ''}
                                  </span>
                                </div>
                                <p className="text-muted-foreground mt-0.5">{e.action}</p>
                                {e.body_changes && !e.body_changes.changed && <p className="text-[10px] text-green-600 mt-0.5">No changes to contract body</p>}
                                {e.body_changes && e.body_changes.changed && (
                                  <p className="text-[10px] text-amber-600 mt-0.5">{e.body_changes.summary}</p>
                                )}
                              </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* ── Documents tab ── */}
              <TabsContent value="documents" className="px-6 py-4 flex-1 overflow-y-auto">
                <div
                  className="border-2 border-dashed rounded-xl p-6 text-center transition-colors hover:border-primary/50 hover:bg-primary/5 cursor-pointer mb-4"
                  onDragOver={e => { e.preventDefault(); e.currentTarget.classList.add('border-primary', 'bg-primary/10'); }}
                  onDragLeave={e => { e.currentTarget.classList.remove('border-primary', 'bg-primary/10'); }}
                  onDrop={e => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('border-primary', 'bg-primary/10');
                    const files = e.dataTransfer.files;
                    if (files.length > 0) {
                      Array.from(files).forEach(file => {
                        const fakeEvent = { target: { files: [file] } };
                        handleDocUpload(fakeEvent);
                      });
                    }
                  }}
                  onClick={() => document.getElementById('cdu')?.click()}
                >
                  <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground/40" />
                  <p className="text-sm font-medium text-muted-foreground">Drag & drop files here</p>
                  <p className="text-xs text-muted-foreground/60 mt-1">or click to browse — up to 20 documents</p>
                </div>
                <input type="file" id="cdu" className="hidden" onChange={handleDocUpload} multiple />
                {(form.documents || []).length > 0 && (
                  <Table>
                    <TableHeader><TableRow><TableHead>File</TableHead><TableHead>Description</TableHead><TableHead>Type</TableHead><TableHead>Uploaded</TableHead><TableHead></TableHead></TableRow></TableHeader>
                    <TableBody>
                      {form.documents.map((d, i) => (
                        <TableRow key={i} className={d.file_type === 'Executed Contract' ? 'bg-green-50' : ''}>
                          <TableCell className="text-xs">
                            <button className="text-primary hover:underline font-medium" onClick={() => {
                              window.open(`${process.env.REACT_APP_BACKEND_URL}/api/data/supplier-contracts/${contractNumber}/documents/${i}/download`, '_blank');
                            }}>{d.file_name}</button>
                          </TableCell>
                          <TableCell className="text-xs">{d.description}</TableCell>
                          <TableCell><Badge variant="outline" className={`text-xs ${d.file_type === 'Executed Contract' ? 'border-green-300 text-green-700 bg-green-50' : ''}`}>{d.file_type}</Badge></TableCell>
                          <TableCell className="text-xs text-muted-foreground">{d.uploaded_by ? `${d.uploaded_by}` : ''}{d.uploaded_at ? ` · ${new Date(d.uploaded_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', timeZone: 'America/Los_Angeles' })}` : ''}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button size="sm" variant="ghost" onClick={() => {
                                window.open(`${process.env.REACT_APP_BACKEND_URL}/api/data/supplier-contracts/${contractNumber}/documents/${i}/download`, '_blank');
                              }} title="Download">
                                <Eye className="h-3.5 w-3.5 text-primary" />
                              </Button>
                              {d.file_type !== 'Executed Contract' && (
                                <Button size="sm" variant="ghost" onClick={() => setForm(p => ({ ...p, documents: p.documents.filter((_, j) => j !== i) }))}>
                                  <Trash2 className="h-3.5 w-3.5 text-destructive" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </TabsContent>

              {/* ── Executed Contract tab ── */}
              {(wfStatus === 'EXECUTED' || wfStatus === 'EXECUTED_BY_CP') && (
                <TabsContent value="executed" className="px-6 py-4 flex-1 overflow-y-auto">
                  <div className="text-center py-4">
                    <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${wfStatus === 'EXECUTED' ? 'bg-green-100' : 'bg-lime-100'}`}>
                      <CheckCircle className={`h-8 w-8 ${wfStatus === 'EXECUTED' ? 'text-green-600' : 'text-lime-600'}`} />
                    </div>
                    <h3 className={`text-lg font-bold mb-2 ${wfStatus === 'EXECUTED' ? 'text-green-700' : 'text-lime-700'}`}>
                      {wfStatus === 'EXECUTED' ? 'Contract Fully Executed' : 'Executed by CP — Awaiting Our Signature'}
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      {form.executed_at ? `Executed on ${fmtTz(form.executed_at)}` : 'This contract has been fully executed.'}
                    </p>
                    <p className="text-sm text-muted-foreground mb-6">All fields are now locked and cannot be modified.</p>
                    {form.signature_method === 'docusign' && (
                      <div className="space-y-3">
                        <Button size="sm" variant="outline" onClick={async () => {
                          try {
                            const r = await api.post(`/api/signatures/check-docusign/${contractNumber}`);
                            if (r.data.status === 'completed' && r.data.has_pdf) {
                              toast.success('Signed document available');
                              load();
                            } else if (r.data.status === 'completed') {
                              toast.info('Signatures complete but PDF not yet available');
                            } else {
                              toast.info(`DocuSign status: ${r.data.status}`);
                            }
                          } catch { toast.error('Failed to check'); }
                        }}>Check DocuSign Status</Button>
                        {form.has_signed_pdf && (
                          <div>
                            <Button className="bg-green-600 hover:bg-green-700" onClick={() => {
                              window.open(`${process.env.REACT_APP_BACKEND_URL}/api/signatures/signed-pdf/${contractNumber}`, '_blank');
                            }}>
                              <Eye className="h-4 w-4 mr-2" /> View Signed PDF
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                    {form.signature_method === 'internal' && (
                      <p className="text-sm text-muted-foreground">Signed internally. Signatures are recorded in the audit trail.</p>
                    )}
                  </div>
                </TabsContent>
              )}
            </Tabs>
          )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Template loader for existing contracts */}
      {tmplOpen && (
        <Dialog open={tmplOpen} onOpenChange={() => setTmplOpen(false)}>
          <DialogContent className="max-w-[90vw] lg:max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><BookOpen className="h-5 w-5 text-primary" /> Load Template</DialogTitle></DialogHeader>
            <TemplatePicker
              onSelect={async (t) => { await handleTemplateInsert(t.content); setTmplOpen(false); }}
              onBlank={() => { setBody(''); setTmplOpen(false); }}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Send to Supplier Dialog */}
      {showSendToSupplier && (
        <Dialog open onOpenChange={() => setShowSendToSupplier(false)}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><ExternalLink className="h-5 w-5 text-primary" /> Send Contract to Counterparty/Supplier</DialogTitle></DialogHeader>
            <p className="text-sm text-muted-foreground mb-3">Send a secure link to the counterparty/supplier for them to review, edit with track changes, and add comments.</p>
            <div className="space-y-3">
              <div><Label className="text-xs">Contact Name</Label>
                <Input value={supplierReviewName} onChange={e => setSupplierReviewName(e.target.value)} placeholder="Supplier contact name" className="h-8 text-sm" /></div>
              <div><Label className="text-xs">Contact Email *</Label>
                <Input value={supplierReviewEmail} onChange={e => setSupplierReviewEmail(e.target.value)} placeholder="supplier@example.com" className="h-8 text-sm" /></div>
              <div><Label className="text-xs">Message</Label>
                <Textarea value={supplierReviewMsg} onChange={e => setSupplierReviewMsg(e.target.value)} rows={3} className="text-sm" /></div>
            </div>
            <div className="flex justify-end gap-2 mt-3">
              <Button size="sm" variant="outline" onClick={() => setShowSendToSupplier(false)}>Cancel</Button>
              <Button size="sm" onClick={sendToSupplier} disabled={sendingToSupplier || !supplierReviewEmail.trim()}>
                {sendingToSupplier ? 'Sending...' : 'Send Review Link'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* View Supplier Review Dialog */}
      {viewingSupplierReview && (
        <SupplierReviewDialog
          session={viewingSupplierReview}
          contractNumber={contractNumber}
          reviewSessions={reviewSessions}
          onAccept={async () => {
            setViewingSupplierReview(null);
            load();
            onSaved();
          }}
          onSendBack={async (authorComments, message) => {
            try {
              await api.post(`/api/contract-review/send/${contractNumber}`, {
                supplier_email: viewingSupplierReview.supplier_email,
                supplier_name: viewingSupplierReview.supplier_name,
                message: message || 'Please review our comments and the updated contract.',
              });
              toast.success('Contract sent back to CP with your edits and comments');
              setViewingSupplierReview(null);
              load();
              onSaved();
              loadReviewSessions();
            } catch (e) { toast.error(getApiErrorMessage(e, 'Send failed')); }
          }}
          onClose={() => setViewingSupplierReview(null)}
        />
      )}
    </>
  );
}


function SupplierReviewDialog({ session, contractNumber, reviewSessions, onAccept, onSendBack, onClose }) {
  const isEditable = session._editable !== false;
  const [authorComment, setAuthorComment] = useState('');
  const [allComments, setAllComments] = useState(session.supplier_comments || []);
  const [sendBackMsg, setSendBackMsg] = useState('Please review our comments and the updated contract.');
  const [sending, setSending] = useState(false);
  const [editedBody, setEditedBody] = useState(session.supplier_body || session.body_snapshot || '');
  const editorRef = useRef(null);
  const initializedRef = useRef(false);

  // Use callback ref to set innerHTML when DOM element is first attached
  const setEditorRef = useCallback((el) => {
    editorRef.current = el;
    if (el && !initializedRef.current) {
      initializedRef.current = true;
      el.innerHTML = session.supplier_body || session.body_snapshot || '';
      el.addEventListener('beforeinput', (e) => {
        if (e.inputType === 'insertText' || e.inputType === 'insertParagraph') {
          document.execCommand('hiliteColor', false, '#fde68a');
        }
        if (e.inputType === 'deleteContentBackward' || e.inputType === 'deleteContentForward') {
          const sel = window.getSelection();
          if (sel.rangeCount > 0) {
            let node = sel.focusNode?.nodeType === 3 ? sel.focusNode.parentElement : sel.focusNode;
            let isAmber = false;
            while (node && node !== el) {
              const bg = node.style?.backgroundColor || '';
              if (bg === 'rgb(253, 230, 138)' || bg === '#fde68a') { isAmber = true; break; }
              node = node.parentElement;
            }
            if (isAmber) return;
          }
          e.preventDefault();
          if (sel.rangeCount > 0) {
            const range = sel.getRangeAt(0);
            if (range.collapsed && e.inputType === 'deleteContentBackward') {
              range.setStart(range.startContainer, Math.max(0, range.startOffset - 1));
            }
            const span = document.createElement('span');
            span.style.backgroundColor = '#fde68a';
            span.style.textDecoration = 'line-through';
            span.style.color = '#92400e';
            try { range.surroundContents(span); } catch {
              document.execCommand('strikethrough', false, null);
              document.execCommand('hiliteColor', false, '#fde68a');
            }
          }
        }
        setEditedBody(el.innerHTML);
      });
      el.addEventListener('focus', () => document.execCommand('hiliteColor', false, '#fde68a'));
      el.addEventListener('keydown', (e) => {
        if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) document.execCommand('hiliteColor', false, '#fde68a');
      });
    }
  }, []); // eslint-disable-line

  const addAuthorComment = () => {
    if (!authorComment.trim()) return;
    const updated = [...allComments, {
      author: 'Contract Author',
      text: authorComment,
      date: new Date().toLocaleString(),
      type: 'author',
    }];
    setAllComments(updated);
    setAuthorComment('');
    // Auto-save comments
    const idx = reviewSessions.findIndex(s => s === session);
    api.post(`/api/contract-review/save-comments/${contractNumber}`, { session_index: idx >= 0 ? idx : 0, comments: updated }).catch(() => {});
  };

  const saveAndSendBack = async () => {
    setSending(true);
    // Save the edited body back to the contract before sending
    try {
      const body = editorRef.current?.innerHTML || editedBody;
      await api.put(`/api/contracts/${contractNumber}/body`, { body });
    } catch {}
    await onSendBack(allComments, sendBackMsg);
    setSending(false);
  };

  const acceptChanges = async () => {
    try {
      // First save any edits the author made
      const body = editorRef.current?.innerHTML || editedBody;
      await api.put(`/api/contracts/${contractNumber}/body`, { body });
      // Then call the accept endpoint which cleans HTML and sets CP_REVIEW_COMPLETE
      const idx = reviewSessions.findIndex(s => s === session);
      await api.post(`/api/contract-review/accept/${contractNumber}`, { session_index: idx >= 0 ? idx : 0 });
      toast.success('CP review complete. All changes accepted.');
      onAccept();
      onClose();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed')); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-[90vw] lg:max-w-5xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5 text-green-600" />
            Review & Edit — {session.supplier_name}'s Changes
          </DialogTitle>
        </DialogHeader>
        <div className="flex items-center gap-4 text-[11px] text-muted-foreground">
          <span className="flex items-center gap-1"><span className="w-3 h-2 rounded bg-green-200 inline-block" /> CP additions</span>
          <span className="flex items-center gap-1"><span className="w-3 h-2 rounded bg-red-200 inline-block" style={{ textDecoration: 'line-through' }} /> CP deletions</span>
          <span className="flex items-center gap-1"><span className="w-3 h-2 rounded bg-amber-200 inline-block" /> Your edits (amber)</span>
          <span className="ml-auto text-[10px]">{isEditable ? 'Edit the document directly — your changes appear in amber' : 'View only — this review has been dealt with'}</span>
        </div>
        <div className="flex gap-4 flex-1 min-h-0 mt-2">
          {/* Contract with track changes */}
          <div className="flex-1 border rounded-lg overflow-y-auto flex flex-col">
            <div className="px-3 py-2 border-b bg-muted/30 flex items-center justify-between">
              <span className="text-xs font-semibold">Contract Document {!isEditable && '(View Only)'}</span>
              {isEditable && (
                <div className="flex gap-1">
                  <button onClick={() => document.execCommand('bold')} className="px-2 py-0.5 text-xs border rounded hover:bg-accent"><b>B</b></button>
                  <button onClick={() => document.execCommand('italic')} className="px-2 py-0.5 text-xs border rounded hover:bg-accent"><i>I</i></button>
                  <button onClick={() => document.execCommand('underline')} className="px-2 py-0.5 text-xs border rounded hover:bg-accent"><u>U</u></button>
                </div>
              )}
            </div>
            <div ref={setEditorRef} contentEditable={isEditable} suppressContentEditableWarning
              className={`flex-1 p-4 prose prose-sm max-w-none outline-none ${!isEditable ? 'opacity-80' : ''}`}
              style={{ fontFamily: "'Georgia', serif", fontSize: '13px', lineHeight: '1.8', minHeight: '300px' }}
              onInput={() => isEditable && setEditedBody(editorRef.current?.innerHTML || '')} />
          </div>
          {/* Comments thread */}
          <div className="w-72 border rounded-lg flex flex-col">
            <div className="px-3 py-2 border-b bg-muted/30 text-xs font-semibold">
              Comments ({allComments.length})
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-2">
              {allComments.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-4">No comments yet</p>
              ) : allComments.map((c, i) => (
                <div key={i} className={`p-2 rounded-lg border text-xs ${c.type === 'author' ? 'bg-blue-50 border-blue-200' : 'bg-card'}`}>
                  <div className="font-semibold text-[11px]">
                    {c.type === 'author' ? (
                      <span className="text-blue-700">{c.author} (You)</span>
                    ) : (
                      <span className="text-green-700">{c.author} (CP)</span>
                    )}
                    <span className="text-muted-foreground font-normal ml-1">{c.date}</span>
                  </div>
                  <p className="mt-1">{c.text}</p>
                </div>
              ))}
            </div>
            <div className="p-2 border-t space-y-1">
              <Textarea value={authorComment} onChange={e => setAuthorComment(e.target.value)}
                rows={2} placeholder="Add your comment..." className="text-xs" />
              <Button size="sm" className="w-full h-7 text-xs bg-blue-600 hover:bg-blue-700" onClick={addAuthorComment} disabled={!authorComment.trim()}>
                Add Comment
              </Button>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between mt-3 pt-3 border-t gap-3">
          {isEditable ? (
            <>
              <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={acceptChanges}>
                <Check className="h-4 w-4 mr-1" /> Accept & Complete CP Review
              </Button>
              <div className="flex items-center gap-2 flex-1">
                <Input value={sendBackMsg} onChange={e => setSendBackMsg(e.target.value)} className="h-8 text-xs flex-1" placeholder="Message to CP..." />
                <Button size="sm" className="bg-blue-600 hover:bg-blue-700 whitespace-nowrap" disabled={sending}
                  onClick={saveAndSendBack}>
                  <Send className="h-3.5 w-3.5 mr-1" /> {sending ? 'Sending...' : 'Save & Send to CP'}
                </Button>
              </div>
            </>
          ) : (
            <p className="text-xs text-muted-foreground">This review has been dealt with. View only.</p>
          )}
          <Button size="sm" variant="ghost" onClick={onClose}>Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function SignaturePanel({ contractNumber, form, loadDefaults, sigName, setSigName, sigEmail, setSigEmail, sigPosition, setSigPosition, onSent }) {
  const [sending, setSending] = useState(false);
  const [ourName, setOurName] = useState('');
  const [ourEmail, setOurEmail] = useState('');

  useEffect(() => {
    loadDefaults();
    // Default our side to contract manager — resolve their email reliably
    const managerName = form?.contract_manager || '';
    setOurName(managerName);
    if (managerName) {
      api.get('/api/users', { params: { page_size: 200 } }).then(r => {
        const users = r.data?.data || r.data?.users || [];
        // Match by full name (how ContractManagerSelect stores it)
        const mgr = users.find(u => `${u.first_name || ''} ${u.last_name || ''}`.trim() === managerName)
          || users.find(u => `${u.first_name || ''} ${u.last_name || ''}`.trim().toLowerCase() === managerName.toLowerCase());
        if (mgr) setOurEmail(mgr.email || '');
      }).catch(() => {});
    }
  }, []); // eslint-disable-line

  const sendSignature = async (method) => {
    if (!sigEmail.trim()) { toast.error('CP signatory email is required'); return; }
    if (!sigName.trim()) { toast.error('CP signatory name is required'); return; }
    if (!ourEmail.trim()) { toast.error('Our signatory email is required'); return; }
    if (!ourName.trim()) { toast.error('Our signatory name is required'); return; }
    setSending(true);
    try {
      const endpoint = method === 'docusign'
        ? `/api/signatures/docusign/send/${contractNumber}`
        : `/api/signatures/internal/send/${contractNumber}`;
      const r = await api.post(endpoint, {
        signatories: [
          { name: sigName, email: sigEmail, position: sigPosition, role: 'cp' },
          { name: ourName, email: ourEmail, role: 'internal' },
        ]
      });
      toast.success(r.data.message);
      onSent();
    } catch (e) { toast.error(getApiErrorMessage(e, 'Failed to send for signature')); }
    setSending(false);
  };

  return (
    <div className="border rounded-xl p-4 bg-emerald-50 space-y-3">
      <p className="text-sm font-semibold text-emerald-700">Send for Execution</p>
      <p className="text-xs text-muted-foreground">All review stages complete. Enter signatory details for both parties.</p>
      
      <div className="border rounded-lg p-3 bg-white space-y-2">
        <p className="text-[11px] font-semibold text-green-700 uppercase">Counterparty Signatory</p>
        <div className="grid grid-cols-3 gap-2">
          <div><Label className="text-[10px]">Name</Label>
            <Input value={sigName} onChange={e => setSigName(e.target.value)} className="h-7 text-xs" placeholder="CP signatory name" /></div>
          <div><Label className="text-[10px]">Email</Label>
            <Input value={sigEmail} onChange={e => setSigEmail(e.target.value)} className="h-7 text-xs" placeholder="email@supplier.com" /></div>
          <div><Label className="text-[10px]">Position</Label>
            <Input value={sigPosition} onChange={e => setSigPosition(e.target.value)} className="h-7 text-xs" placeholder="e.g. Director" /></div>
        </div>
      </div>

      <div className="border rounded-lg p-3 bg-white space-y-2">
        <p className="text-[11px] font-semibold text-blue-700 uppercase">Our Signatory</p>
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-[10px]">Name</Label>
            <Input value={ourName} onChange={e => setOurName(e.target.value)} className="h-7 text-xs" placeholder="Our signatory name" /></div>
          <div><Label className="text-[10px]">Email</Label>
            <Input value={ourEmail} onChange={e => setOurEmail(e.target.value)} className="h-7 text-xs" placeholder="email@company.com" /></div>
        </div>
      </div>

      <div className="flex gap-2">
        <Button size="sm" className="flex-1 bg-emerald-600 hover:bg-emerald-700" disabled={sending || !sigEmail.trim() || !ourEmail.trim()}
          onClick={() => sendSignature('internal')}>
          {sending ? 'Sending...' : 'Internal Signature'}
        </Button>
        <Button size="sm" className="flex-1 bg-blue-600 hover:bg-blue-700" disabled={sending || !sigEmail.trim() || !ourEmail.trim()}
          onClick={() => sendSignature('docusign')}>
          {sending ? 'Sending...' : 'DocuSign'}
        </Button>
      </div>
    </div>
  );
}
